import { IToken } from '@src/types/token'

// Helper function to compare gas tokens priority
export const compareGasTokens = (a: IToken, b: IToken): number => {
    // Native tokens (like ETH) should come first
    if (a.native && !b.native) return -1
    if (!a.native && b.native) return 1

    // Then wrapped versions (like WETH)
    if (a.wrapped && !b.wrapped) return -1
    if (!a.wrapped && b.wrapped) return 1

    return 0
}

// Helper function to sort tokens by priority
export const sortTokensByPriority = (tokens: IToken[], query?: string): IToken[] => {
    return tokens.sort((a, b) => {
        // First prioritize gas tokens
        const gasTokenComparison = compareGasTokens(a, b)
        if (gasTokenComparison !== 0) return gasTokenComparison

        // Then sort by exact symbol match if query exists
        if (query) {
            const lowercaseQuery = query.toLowerCase()
            const aExactMatch = a.symbol.toLowerCase() === lowercaseQuery
            const bExactMatch = b.symbol.toLowerCase() === lowercaseQuery
            if (aExactMatch && !bExactMatch) return -1
            if (!aExactMatch && bExactMatch) return 1
        }

        // Then sort by user balance
        const aBalance = a.userBalanceInUSD ?? 0
        const bBalance = b.userBalanceInUSD ?? 0
        if (aBalance && bBalance) {
            return bBalance - aBalance
        } else if (aBalance) {
            return -1
        } else if (bBalance) {
            return 1
        }

        // Finally sort by market cap
        const aCap = a.market_cap ?? -Infinity
        const bCap = b.market_cap ?? -Infinity
        return bCap - aCap
    })
}

// Helper function to filter tokens by selected token
export const filterBySelectedToken = (tokens: IToken[], selectedToken?: IToken, side?: 'buy' | 'sell'): IToken[] => {
    if (!selectedToken || !side) return tokens

    return tokens.filter(token => {
        // Don't filter out tokens on different chains
        if (token.chainId !== selectedToken.chainId) return true

        // Don't filter if addresses are different
        if (token.address.toLowerCase() !== selectedToken.address.toLowerCase()) return true

        // Filter out the token with same address and chain based on side
        return false
    })
}

// Helper function to filter tokens by search query
export const filterTokensByQuery = (
    tokens: IToken[],
    query: string,
    selectedToken?: IToken,
    side?: 'buy' | 'sell'
): IToken[] => {
    const lowercaseQuery = query.toLowerCase()

    return tokens.filter(token => {
        const matchesSearch =
            token.name.toLowerCase().includes(lowercaseQuery) || token.symbol.toLowerCase().includes(lowercaseQuery)

        if (!matchesSearch) return false

        // Additional filtering for selected token
        return filterBySelectedToken([token], selectedToken, side).length > 0
    })
}
